import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ConditionsList } from './ConditionsList';
import { DependenciesTab } from './DependenciesTab';
import {
  QuestionnaireFormData,
  type QuestionConditionStats,
} from '@/features/questionnaires';
import type { Question } from '@aphp-ema/common/questionnaire';
import { useFormContext } from 'react-hook-form';

interface ConditionsModalProps {
  open: boolean;
  onClose: () => void;
  questionIndex: number;
  questionStats?: QuestionConditionStats;
  allQuestions?: Question[];
  questions: Question[];
}

export const ConditionsModal: React.FC<ConditionsModalProps> = ({
  open,
  onClose,
  questionIndex,
  questionStats,
  allQuestions,
  questions,
}) => {
  const { trigger } = useFormContext<QuestionnaireFormData>();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const previousQuestions = questions.slice(0, questionIndex);

  const handleSave = async () => {
    const success = await trigger(
      `questions.${questionIndex}.displayConditions`,
    );
    if (success) {
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">
            {t('pages.studies.questions.conditions.title')}{' '}
            {questions?.[questionIndex]?.label ||
              `${t('pages.studies.questions.title', { index: questionIndex + 1 })}`}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab
            icon={<VisibilityIcon />}
            iconPosition="start"
            label={t('pages.studies.questions.conditions.displayConditionsTab')}
          />
          <Tab
            icon={<LinkIcon />}
            iconPosition="start"
            label={t('pages.studies.questions.conditions.dependenciesTab')}
            disabled={
              !questionStats || questionStats.dependentQuestionsCount === 0
            }
          />
        </Tabs>
      </Box>

      <DialogContent>
        {activeTab === 0 && (
          <ConditionsList
            questionIndex={questionIndex}
            previousQuestions={previousQuestions}
          />
        )}

        {activeTab === 1 && (
          <DependenciesTab
            questionStats={questionStats}
            allQuestions={allQuestions}
            currentQuestionIndex={questionIndex}
            currentQuestionLabel={questions?.[questionIndex]?.label}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} size="large">
          {activeTab === 0 ? t('buttons.cancel') : t('buttons.close')}
        </Button>
        {activeTab === 0 && (
          <Button onClick={handleSave} variant="contained" size="large">
            {t('pages.studies.questions.conditions.saveConditions')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Backward compatibility export
export const QuestionConditionsModal = ConditionsModal;
